/**
 * The L2 module tab sets of the admin console — data only, no JSX, so the tab
 * row (admin-module-tabs.tsx) and the admin command palette's search index
 * (admin-nav-data.tsx) share ONE registry (Critical Rule #8).
 *
 * The 2026-07-05 admin regroup shrank the sidebar to 6 groups / ~30 items by
 * absorbing facet pages into their module as in-page tabs. Every absorbed
 * route keeps its URL — only its nav entry moved from the sidebar into the
 * module's tab row. Labels are plain strings: the admin console is
 * intentionally not translated (same convention as `admin-nav-data.tsx`).
 *
 * Coverage: `admin-nav-coverage.test.ts` scans the `href:` entries below, so a
 * tab pointing at a route with no `page.tsx` — or an absorbed top-level module
 * missing from both the sidebar and this registry — fails the unit test.
 */

export interface AdminModuleTab {
  label: string;
  href: string;
  /**
   * Active-state matching: omitted = exact match (the default suits tabs
   * whose pages mount the bar only on the listed route). `false` = stay
   * active on sub-routes (used where the tab's route redirects to or hosts a
   * deeper rendered surface, e.g. /admin/settings → settings/feature-flags).
   */
  end?: boolean;
}

export type AdminModule =
  | 'artists'
  | 'auditionImports'
  | 'automation'
  | 'casting'
  | 'competitions'
  | 'productions'
  | 'communications'
  | 'invoices'
  | 'partners'
  | 'usersRoles'
  | 'settings'
  | 'support'
  | 'compliance';

export const ADMIN_MODULE_TABS: Record<AdminModule, AdminModuleTab[]> = {
  automation: [
    { label: 'Campaigns', href: '/admin/automation/campaigns' },
    { label: 'A/B Tests', href: '/admin/automation/ab-tests' },
    { label: 'Analytics', href: '/admin/automation/analytics' },
  ],
  artists: [
    { label: 'Directory', href: '/admin/artists' },
    { label: 'Map', href: '/admin/artists/map' },
    { label: 'Analytics', href: '/admin/artist-analytics' },
    { label: 'Fever Ratings', href: '/admin/fever-ratings' },
    {
      label: 'ID Verification',
      href: '/admin/legal-compliance/identity-verifications',
    },
  ],
  casting: [
    { label: 'Hire Orders', href: '/admin/hire-orders' },
    { label: 'Dance Assistants', href: '/admin/dance-assistants' },
    { label: 'Coaching', href: '/admin/coaching' },
  ],
  competitions: [
    { label: 'Competitions', href: '/admin/competitions' },
    // Competition scholarship offers — a read-only cross-competition overview
    // (per-competition management lives on the organizer surface).
    { label: 'Offers', href: '/admin/competitions/offers' },
  ],
  // External-audition ingestion pipeline: the source registry feeds the import
  // review queue. Distinct from competitions and hire offers.
  auditionImports: [
    { label: 'Sources', href: '/admin/audition-sources' },
    { label: 'Imports', href: '/admin/audition-imports' },
  ],
  productions: [
    { label: 'Productions', href: '/admin/productions' },
    { label: 'Repertoire', href: '/admin/repertoire' },
    { label: 'Casting Pools', href: '/admin/casting-pools' },
  ],
  communications: [
    { label: 'Communications', href: '/admin/communications' },
    { label: 'WhatsApp Timeline', href: '/admin/communication-timeline' },
  ],
  invoices: [
    { label: 'Artist', href: '/admin/finance/artist-invoices' },
    {
      label: 'Self-billing',
      href: '/admin/finance/artist-invoices/self-billing',
    },
    { label: 'Client', href: '/admin/finance/client-invoices' },
    { label: 'Monthly', href: '/admin/finance/invoices/monthly' },
    { label: 'Analytics', href: '/admin/finance/invoices/analytics' },
  ],
  partners: [
    { label: 'Clients', href: '/admin/clients' },
    { label: 'Agencies', href: '/admin/agencies' },
    { label: 'Organizations', href: '/admin/organizations' },
    { label: 'Join requests', href: '/admin/organizations/join-requests' },
    { label: 'Accounts', href: '/admin/accounts' },
  ],
  usersRoles: [
    { label: 'Users', href: '/admin/users' },
    { label: 'Roles', href: '/admin/roles' },
  ],
  settings: [
    // /admin/settings redirects to its feature-flags sub-page. Each settings
    // sub-page has its own tab, matched exactly.
    { label: 'Feature Flags', href: '/admin/settings/feature-flags' },
    { label: 'Billing Entities', href: '/admin/settings/billing-entities' },
    { label: 'Categories', href: '/admin/categories' },
    { label: 'Automations', href: '/admin/automations' },
    { label: 'Data Sync', href: '/admin/sync' },
    { label: 'Maintenance', href: '/admin/maintenance' },
  ],
  support: [
    // The dedicated /admin/support inbox was retired — Ballee org members handle
    // support from their normal Inbox (support→Ballee-org, Phase 4b). Feedback +
    // Issues are Flow-linked and stay.
    { label: 'Feedback', href: '/admin/feedback' },
    { label: 'Issues', href: '/admin/issues' },
  ],
  compliance: [
    { label: 'Legal', href: '/admin/legal-compliance' },
    { label: 'Audit', href: '/admin/audit' },
  ],
};
